import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AdminHeader from "../../../../components/AdminHeader";
import AdminPageHeader from "../../../../components/admin/AdminPageHeader";
import apiFetch from "../../../../utils/apiFetch";
import { adminListMedia } from "../../../../services/admin";

const BLOCK_TYPES = [
  { value: "text", label: "Texto" },
  { value: "audio", label: "Audio" },
  { value: "video", label: "Video" },
  { value: "download", label: "Descargable" },
];

const BLOCK_PRESETS = [
  {
    value: "momento_de_verdad",
    label: "1. Momentos de verdad",
    type: "audio",
    title: "1. MOMENTOS DE VERDAD",
    position: 1,
  },
  {
    value: "alineamiento_interno",
    label: "2. Alineamiento interno",
    type: "text",
    title: "2. ALINEAMIENTO INTERNO",
    position: 2,
  },
  {
    value: "poder_ejecucion",
    label: "3. Toma acción",
    type: "text",
    title: "3. TOMA ACCIÓN",
    position: 3,
    hasMicroMission: true,
  },
  {
    value: "vida_reino",
    label: "4. Vida de Reino",
    type: "download",
    title: "4. VIDA DE REINO",
    position: 4,
  },
  {
    value: "reflexion",
    label: "5. Reflexión",
    type: "audio",
    title: "5. REFLEXIÓN",
    position: 5,
  },
  {
    value: "custom",
    label: "Bloque personalizado",
    type: "text",
    title: "",
    position: null,
  },
];

const LINE_TYPES = [
  { value: "none", label: "Ninguno" },
  { value: "paragraph", label: "Párrafo" },
  { value: "dash", label: "Rayita —" },
  { value: "dot", label: "Punto •" },
  { value: "check", label: "Check ✓" },
  { value: "number", label: "Numerada 1." },
  { value: "quote", label: "Frase destacada" },
];

const ICON_STYLES = [
  { value: "none", label: "Sin ícono" },
  { value: "book", label: "Libro" },
  { value: "check", label: "Check" },
  { value: "arrow", label: "Flecha" },
  { value: "light", label: "Luz" },
  { value: "heart", label: "Corazón" },
  { value: "crown", label: "Corona" },
  { value: "pen", label: "Lápiz" },
];

const EMPHASIS_STYLES = [
  { value: "none", label: "Sin destaque" },
  { value: "bold", label: "Frase en negrita" },
  { value: "quote", label: "Frase destacada" },
];

function makeLine(overrides = {}) {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    kind: "paragraph",
    icon: "none",
    text: "",
    ...overrides,
  };
}

function makeMissionBlock(overrides = {}) {
  return {
    id: crypto.randomUUID(),
    kind: "text",
    text: "",
    placeholder: "",
    examples: [],
    checklist: [],
    ...overrides,
  };
}

function makeMissionStep(number, overrides = {}) {
  return {
    id: crypto.randomUUID(),
    number,
    title: `Paso ${number}`,
    blocks: [makeMissionBlock()],
    ...overrides,
  };
}

function inferPresetFromTitle(title = "") {
  const clean = title.trim().toUpperCase();

  const match = BLOCK_PRESETS.find(
    (preset) => preset.title?.trim().toUpperCase() === clean
  );

  return match?.value || "custom";
}

function parseLineToken(line) {
  const clean = line.trim();

  if (!clean) return null;

  const iconRegex = /\[\[ICON:(book|check|arrow|light|heart|crown|pen)\]\]\s*/;

  if (clean.startsWith("[[QUOTE]]")) {
    let text = clean.replace("[[QUOTE]]", "").trim();
    let icon = "none";

    const iconMatch = text.match(iconRegex);

    if (iconMatch) {
      icon = iconMatch[1];
      text = text.replace(iconRegex, "").trim();
    }

    return makeLine({
      kind: "quote",
      icon,
      text,
    });
  }

  const bulletMatch = clean.match(/^\[\[BULLET:(dot|check|dash|number)\]\]\s*/);

  if (bulletMatch) {
    let text = clean.replace(/^\[\[BULLET:(dot|check|dash|number)\]\]\s*/, "");
    let icon = "none";

    const iconMatch = text.match(iconRegex);

    if (iconMatch) {
      icon = iconMatch[1];
      text = text.replace(iconRegex, "").trim();
    }

    return makeLine({
      kind: bulletMatch[1],
      icon,
      text: text.trim(),
    });
  }

  let text = clean;
  let icon = "none";

  const iconMatch = text.match(iconRegex);

  if (iconMatch) {
    icon = iconMatch[1];
    text = text.replace(iconRegex, "").trim();

    return makeLine({
      kind: "none",
      icon,
      text,
    });
  }

  return makeLine({
    kind: "paragraph",
    icon: "none",
    text,
  });
}

function parseExistingContent(content = "", preset = "custom") {
  const normalized = String(content || "").replace(/\r\n/g, "\n").trim();

  const parsed = {
    bodyText: "",
    scriptureReference: "",
    scriptureText: "",
    applicationText: "",
    closingText: "",
    resourceNote: "",
    emphasisStyle: "bold",
    lines: [],
  };

  if (!normalized) return parsed;

  const sections = normalized
    .split(/\n\s*---+\s*\n/g)
    .map((section) => section.trim())
    .filter(Boolean);

  if (preset === "momento_de_verdad") {
    const scriptureSection = sections[0] || "";
    const applicationSection = sections[1] || "";

    const scriptureLines = scriptureSection
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (scriptureLines[0]?.startsWith("[[SCRIPTURE_REFERENCE]]")) {
      parsed.scriptureReference = scriptureLines[0]
        .replace("[[SCRIPTURE_REFERENCE]]", "")
        .trim();

      parsed.scriptureText = scriptureLines.slice(1).join("\n\n").trim();
    } else {
      parsed.scriptureText = scriptureSection;
    }

    const appLines = applicationSection
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const lastLine = appLines[appLines.length - 1] || "";

    if (lastLine.startsWith("[[QUOTE]]")) {
      parsed.emphasisStyle = "quote";
      parsed.closingText = lastLine.replace("[[QUOTE]]", "").trim();
      parsed.applicationText = appLines.slice(0, -1).join("\n\n").trim();
    } else if (lastLine.startsWith("**") && lastLine.endsWith("**")) {
      parsed.emphasisStyle = "bold";
      parsed.closingText = lastLine.slice(2, -2).trim();
      parsed.applicationText = appLines.slice(0, -1).join("\n\n").trim();
    } else {
      parsed.applicationText = applicationSection.trim();
    }

    return parsed;
  }

  const lines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const working = [...lines];
  const lastLine = working[working.length - 1] || "";

  if (lastLine.startsWith("[[QUOTE]]")) {
    parsed.emphasisStyle = "quote";
    parsed.closingText = lastLine.replace("[[QUOTE]]", "").trim();
    working.pop();
  } else if (lastLine.startsWith("**") && lastLine.endsWith("**")) {
    parsed.emphasisStyle = "bold";
    parsed.closingText = lastLine.slice(2, -2).trim();
    working.pop();
  }

  const paragraphBuffer = [];
  const structuredLines = [];

  working.forEach((line) => {
    if (line.startsWith("[[BULLET:") || line.startsWith("[[QUOTE]]")) {
      structuredLines.push(parseLineToken(line));
      return;
    }

    const parsedLine = parseLineToken(line);

    if (parsedLine) {
      structuredLines.push(parsedLine);
    } else {
      paragraphBuffer.push(line);
    }
  });

  parsed.bodyText = paragraphBuffer.join("\n\n").trim();
  parsed.lines = structuredLines.filter(Boolean);

  return parsed;
}

export default function AdminItems() {
  const { systemId, sectionId, lessonId } = useParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [mediaItems, setMediaItems] = useState([]);
  const [mediaSearch, setMediaSearch] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingPosition, setEditingPosition] = useState(null);

  const [preset, setPreset] = useState("custom");
  const [type, setType] = useState("text");
  const [title, setTitle] = useState("");

  const [hasMicroMission, setHasMicroMission] = useState(false);
  const [missionButtonLabel, setMissionButtonLabel] =
    useState("Pasar a la acción");
  const [showMissionEditor, setShowMissionEditor] = useState(false);

  const [bodyText, setBodyText] = useState("");
  const [scriptureReference, setScriptureReference] = useState("");
  const [scriptureText, setScriptureText] = useState("");
  const [applicationText, setApplicationText] = useState("");
  const [closingText, setClosingText] = useState("");
  const [resourceNote, setResourceNote] = useState("");
  const [emphasisStyle, setEmphasisStyle] = useState("bold");

  const [lines, setLines] = useState([makeLine()]);

  const [missionSteps, setMissionSteps] = useState([
    makeMissionStep(1),
    makeMissionStep(2),
    makeMissionStep(3),
    makeMissionStep(4),
    makeMissionStep(5),
  ]);

  const [missionClosing, setMissionClosing] = useState({
    title: "Altar de Testimonio",
    blocks: [makeMissionBlock()],
  });

  const [selectedMedia, setSelectedMedia] = useState(null);
  const [manualMediaUrl, setManualMediaUrl] = useState("");

  async function loadItems() {
    try {
      const data = await apiFetch(
        `/api/systems/${systemId}/sections/${sectionId}/lessons/${lessonId}/items`
      );
      setItems(data.items || []);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMedia(searchValue = "") {
    try {
      const data = await adminListMedia(searchValue);
      setMediaItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      console.error("cannot load media", e);
    }
  }

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadMedia(mediaSearch);
    }, 250);

    return () => clearTimeout(timer);
  }, [mediaSearch]);

  const filteredMedia = useMemo(() => {
    const q = mediaSearch.trim().toLowerCase();

    return mediaItems.filter((item) => {
      if (!q) return true;

      return (
        item.title?.toLowerCase().includes(q) ||
        item.type?.toLowerCase().includes(q) ||
        item.provider?.toLowerCase().includes(q)
      );
    });
  }, [mediaItems, mediaSearch]);

  function getNumberFromTitle(value = "") {
    const match = String(value).trim().match(/^(\d+)[\.\)]?\s+/);
    return match ? Number(match[1]) : null;
  }

  function getPresetPosition() {
    const selected = BLOCK_PRESETS.find((item) => item.value === preset);
    return selected?.position || null;
  }

  function getNextPosition() {
    const positions = items
      .map((item) => Number(item.position))
      .filter((value) => Number.isFinite(value));

    if (!positions.length) return 1;

    return Math.max(...positions) + 1;
  }

  function getPositionForPayload() {
    const numberFromTitle = getNumberFromTitle(title);

    if (numberFromTitle) return numberFromTitle;

    const presetPosition = getPresetPosition();

    if (!editingId && presetPosition) return presetPosition;

    if (editingId && editingPosition) return editingPosition;

    return getNextPosition();
  }

  function getDefaultMission() {
    return {
      kind: "micro_mission",
      version: 1,
      steps: [
        makeMissionStep(1),
        makeMissionStep(2),
        makeMissionStep(3),
        makeMissionStep(4),
        makeMissionStep(5),
      ],
      closing: {
        title: "Altar de Testimonio",
        blocks: [makeMissionBlock()],
      },
    };
  }

  function parseLessonBlockContent(content = "") {
    const raw = String(content || "");

    try {
      const parsed = JSON.parse(raw);

      if (parsed?.kind === "lesson_block") {
        return {
          body: parsed.body || "",
          hasMicroMission: Boolean(parsed.features?.microMission?.enabled),
          missionButtonLabel:
            parsed.features?.microMission?.buttonLabel || "Pasar a la acción",
          mission: parsed.features?.microMission?.mission || getDefaultMission(),
        };
      }

      if (parsed?.kind === "micro_mission_block") {
        return {
          body: parsed.visible?.text || "",
          hasMicroMission: true,
          missionButtonLabel: parsed.visible?.buttonLabel || "Pasar a la acción",
          mission: parsed.mission || getDefaultMission(),
        };
      }

      if (parsed?.kind === "micro_mission") {
        return {
          body:
            "Hoy vas a aplicar esta verdad en una acción concreta. No necesitas resolverlo todo. Solo necesitas dar un paso real.",
          hasMicroMission: true,
          missionButtonLabel: "Pasar a la acción",
          mission: parsed,
        };
      }
    } catch (err) {
      // Contenido normal antiguo: texto plano.
    }

    return {
      body: raw,
      hasMicroMission: false,
      missionButtonLabel: "Pasar a la acción",
      mission: getDefaultMission(),
    };
  }

  const orderedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const positionA = Number(a.position ?? 999999);
      const positionB = Number(b.position ?? 999999);

      if (positionA !== positionB) return positionA - positionB;

      return Number(a.id) - Number(b.id);
    });
  }, [items]);

  function handlePresetChange(value) {
    setPreset(value);

    const selected = BLOCK_PRESETS.find((p) => p.value === value);
    if (!selected) return;

    setType(selected.type);
    setTitle(selected.title);

    setBodyText("");
    setScriptureReference("");
    setScriptureText("");
    setApplicationText("");
    setClosingText("");
    setResourceNote("");
    setEmphasisStyle("bold");
    setLines([makeLine()]);

    setHasMicroMission(Boolean(selected.hasMicroMission));
    setMissionButtonLabel("Pasar a la acción");
    setShowMissionEditor(Boolean(selected.hasMicroMission));

    setMissionSteps([
      makeMissionStep(1),
      makeMissionStep(2),
      makeMissionStep(3),
      makeMissionStep(4),
      makeMissionStep(5),
    ]);

    setMissionClosing({
      title: "Altar de Testimonio",
      blocks: [makeMissionBlock()],
    });

    if (selected.value === "poder_ejecucion") {
      setBodyText("Hoy vas a aplicar esta verdad en una acción concreta.");

      setLines([
        makeLine({
          kind: "quote",
          icon: "none",
          text: "No necesitas resolverlo todo. Solo necesitas dar un paso real.",
        }),
      ]);
    }
  }

  function updateLine(lineId, patch) {
    setLines((prev) =>
      prev.map((line) => (line.id === lineId ? { ...line, ...patch } : line))
    );
  }

  function addLine(afterId = null) {
    const newLine = makeLine();

    if (!afterId) {
      setLines((prev) => [...prev, newLine]);
      return;
    }

    setLines((prev) => {
      const index = prev.findIndex((line) => line.id === afterId);
      if (index === -1) return [...prev, newLine];

      const next = [...prev];
      next.splice(index + 1, 0, newLine);
      return next;
    });
  }

  function removeLine(lineId) {
    setLines((prev) => {
      const next = prev.filter((line) => line.id !== lineId);
      return next.length ? next : [makeLine()];
    });
  }

  function moveLine(lineId, direction) {
    setLines((prev) => {
      const index = prev.findIndex((line) => line.id === lineId);
      if (index === -1) return prev;

      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;

      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function serializeLine(line, numberIndexRef) {
    const text = line.text.trim();
    if (!text) return "";

    const iconToken = line.icon !== "none" ? `[[ICON:${line.icon}]] ` : "";

    if (line.kind === "none") {
      return `${iconToken}${text}`.trim();
    }

    if (line.kind === "paragraph") {
      return `${iconToken}${text}`.trim();
    }

    if (line.kind === "quote") {
      return `[[QUOTE]] ${iconToken}${text}`.trim();
    }

    if (["dash", "dot", "check", "number"].includes(line.kind)) {
      if (line.kind === "number") numberIndexRef.current += 1;
      return `[[BULLET:${line.kind}]] ${iconToken}${text}`;
    }

    return `${iconToken}${text}`.trim();
  }

  function buildClosingLine(text, emphasis) {
    const clean = text.trim();
    if (!clean) return "";

    if (emphasis === "quote") return `[[QUOTE]] ${clean}`;
    if (emphasis === "bold") return `**${clean}**`;
    return clean;
  }

  function buildContentPayload() {
    const parts = [];
    const numberIndexRef = { current: 0 };

    const renderedLines = lines
      .map((line) => serializeLine(line, numberIndexRef))
      .filter(Boolean);

    if (preset === "momento_de_verdad") {
      if (scriptureReference.trim()) {
        parts.push(`[[SCRIPTURE_REFERENCE]] ${scriptureReference.trim()}`);
      }

      if (scriptureText.trim()) {
        parts.push(scriptureText.trim());
      }

      const appParts = [];

      if (applicationText.trim()) {
        appParts.push(applicationText.trim());
      }

      if (renderedLines.length) {
        appParts.push(...renderedLines);
      }

      const closing = buildClosingLine(closingText, emphasisStyle);
      if (closing) {
        appParts.push(closing);
      }

      if (appParts.length) {
        parts.push("---");
        parts.push(appParts.join("\n\n"));
      }
    } else {
      if (bodyText.trim()) {
        parts.push(bodyText.trim());
      }

      if (renderedLines.length) {
        parts.push(...renderedLines);
      }

      const closing = buildClosingLine(closingText, emphasisStyle);
      if (closing) {
        parts.push(closing);
      }

      if (preset === "vida_reino" && resourceNote.trim()) {
        parts.push(resourceNote.trim());
      }
    }

    const body = parts.join("\n\n").trim();

    if (!hasMicroMission) {
      return body;
    }

    return JSON.stringify(
      {
        kind: "lesson_block",
        version: 1,
        body,
        features: {
          microMission: {
            enabled: true,
            buttonLabel: missionButtonLabel.trim() || "Pasar a la acción",
            mission: {
              kind: "micro_mission",
              version: 1,
              steps: missionSteps,
              closing: missionClosing,
            },
          },
        },
      },
      null,
      2
    );
  }

  const contentPreview = buildContentPayload();

  function getSelectedMediaUrl() {
    return selectedMedia?.url || manualMediaUrl.trim() || "";
  }

  function resetForm() {
    setEditingId(null);
    setEditingPosition(null);
    setPreset("custom");
    setType("text");
    setTitle("");
    setHasMicroMission(false);
    setMissionButtonLabel("Pasar a la acción");
    setShowMissionEditor(false);
    setBodyText("");
    setScriptureReference("");
    setScriptureText("");
    setApplicationText("");
    setClosingText("");
    setResourceNote("");
    setEmphasisStyle("bold");
    setLines([makeLine()]);

    setMissionSteps([
      makeMissionStep(1),
      makeMissionStep(2),
      makeMissionStep(3),
      makeMissionStep(4),
      makeMissionStep(5),
    ]);

    setMissionClosing({
      title: "Altar de Testimonio",
      blocks: [makeMissionBlock()],
    });

    setSelectedMedia(null);
    setManualMediaUrl("");
    setMediaSearch("");
  }

  async function createOrUpdateItem(e) {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = {
        type,
        title: title.trim(),
        content: buildContentPayload(),
        media_url: getSelectedMediaUrl(),
        position: getPositionForPayload(),
      };

      if (editingId) {
        await apiFetch(
          `/api/systems/${systemId}/sections/${sectionId}/lessons/${lessonId}/items/${editingId}`,
          {
            method: "PUT",
            body: payload,
          }
        );
      } else {
        await apiFetch(
          `/api/systems/${systemId}/sections/${sectionId}/lessons/${lessonId}/items`,
          {
            method: "POST",
            body: payload,
          }
        );
      }

      resetForm();
      loadItems();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeItem(id) {
    if (!window.confirm("¿Eliminar bloque?")) return;

    try {
      await apiFetch(
        `/api/systems/${systemId}/sections/${sectionId}/lessons/${lessonId}/items/${id}`,
        {
          method: "DELETE",
        }
      );

      loadItems();
    } catch (err) {
      alert(err.message);
    }
  }

  function startEdit(item) {
    const inferredPreset = inferPresetFromTitle(item.title || "");
    const parsedBlock = parseLessonBlockContent(item.content || "");
    const parsed = parseExistingContent(parsedBlock.body || "", inferredPreset);

    setEditingId(item.id);
    setEditingPosition(item.position ?? null);
    setPreset(inferredPreset);
    setType(item.type === "micro_mission" ? "text" : item.type || "text");
    setTitle(item.title || "");

    setHasMicroMission(parsedBlock.hasMicroMission);
    setMissionButtonLabel(parsedBlock.missionButtonLabel || "Pasar a la acción");
    setShowMissionEditor(false);

    setBodyText(parsed.bodyText || "");
    setScriptureReference(parsed.scriptureReference || "");
    setScriptureText(parsed.scriptureText || "");
    setApplicationText(parsed.applicationText || "");
    setClosingText(parsed.closingText || "");
    setResourceNote(parsed.resourceNote || "");
    setEmphasisStyle(parsed.emphasisStyle || "bold");
    setLines(parsed.lines?.length ? parsed.lines : [makeLine()]);

    setMissionSteps(
      parsedBlock.mission?.steps?.length
        ? parsedBlock.mission.steps
        : [
            makeMissionStep(1),
            makeMissionStep(2),
            makeMissionStep(3),
            makeMissionStep(4),
            makeMissionStep(5),
          ]
    );

    setMissionClosing(
      parsedBlock.mission?.closing || {
        title: "Altar de Testimonio",
        blocks: [makeMissionBlock()],
      }
    );

    setSelectedMedia(
      item.media_url
        ? {
            title: item.title || "Media vinculada",
            url: item.media_url,
          }
        : null
    );

    setManualMediaUrl(item.media_url || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const showScripture = preset === "momento_de_verdad";
  const showApplicationText = preset === "momento_de_verdad";
  const showBodyText = preset !== "momento_de_verdad";
  const showResourceNote = preset === "vida_reino";

  function updateMissionStep(stepId, updates) {
    setMissionSteps((current) =>
      current.map((step) =>
        step.id === stepId ? { ...step, ...updates } : step
      )
    );
  }

  function addMissionBlock(stepId) {
    setMissionSteps((current) =>
      current.map((step) =>
        step.id === stepId
          ? {
              ...step,
              blocks: [...step.blocks, makeMissionBlock()],
            }
          : step
      )
    );
  }

  function updateMissionBlock(stepId, blockId, updates) {
    setMissionSteps((current) =>
      current.map((step) =>
        step.id === stepId
          ? {
              ...step,
              blocks: step.blocks.map((block) =>
                block.id === blockId ? { ...block, ...updates } : block
              ),
            }
          : step
      )
    );
  }

  function removeMissionBlock(stepId, blockId) {
    setMissionSteps((current) =>
      current.map((step) =>
        step.id === stepId
          ? {
              ...step,
              blocks:
                step.blocks.length > 1
                  ? step.blocks.filter((block) => block.id !== blockId)
                  : step.blocks,
            }
          : step
      )
    );
  }

  function updateMissionClosing(updates) {
    setMissionClosing((current) => ({
      ...current,
      ...updates,
    }));
  }

  function addMissionClosingBlock() {
    setMissionClosing((current) => ({
      ...current,
      blocks: [...current.blocks, makeMissionBlock()],
    }));
  }

  function updateMissionClosingBlock(blockId, updates) {
    setMissionClosing((current) => ({
      ...current,
      blocks: current.blocks.map((block) =>
        block.id === blockId ? { ...block, ...updates } : block
      ),
    }));
  }

  function removeMissionClosingBlock(blockId) {
    setMissionClosing((current) => ({
      ...current,
      blocks:
        current.blocks.length > 1
          ? current.blocks.filter((block) => block.id !== blockId)
          : current.blocks,
    }));
  }

  return (
    <>
      <AdminHeader />

      <div style={{ padding: "26px 32px", maxWidth: 1100, margin: "0 auto" }}>
        <AdminPageHeader
          title="Contenido del día"
          subtitle="Editor guiado para construir la experiencia premium del miembro."
          backTo={`/admin/systems/${systemId}/sections/${sectionId}/lessons`}
        />

        <form onSubmit={createOrUpdateItem} style={formBox}>
          <div style={grid2}>
            <div>
              <label style={labelStyle}>Plantilla de bloque</label>
              <select
                value={preset}
                onChange={(e) => handlePresetChange(e.target.value)}
                style={inputStyle}
              >
                {BLOCK_PRESETS.map((presetOption) => (
                  <option key={presetOption.value} value={presetOption.value}>
                    {presetOption.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Tipo</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={inputStyle}
              >
                {BLOCK_TYPES.map((typeOption) => (
                  <option key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Título visible del bloque</label>
            <input
              placeholder="Ej: 1. MOMENTOS DE VERDAD"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              style={inputStyle}
            />
          </div>

          <div style={editorSection}>
            <div style={sectionTitle}>Funcionalidad opcional</div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontWeight: 700,
                color: "#334155",
              }}
            >
              <input
                type="checkbox"
                checked={hasMicroMission}
                onChange={(e) => {
                  setHasMicroMission(e.target.checked);
                  setShowMissionEditor(e.target.checked);
                }}
              />
              Agregar micro-misión a este bloque
            </label>

            {hasMicroMission && (
              <>
                <div>
                  <label style={labelStyle}>Texto del botón de micro-misión</label>
                  <input
                    placeholder="Ej: Pasar a la acción"
                    value={missionButtonLabel}
                    onChange={(e) => setMissionButtonLabel(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setShowMissionEditor((value) => !value)}
                  style={secondaryBtn}
                >
                  {showMissionEditor
                    ? "Ocultar configuración"
                    : "Configurar micro-misión"}
                </button>
              </>
            )}
          </div>

          {hasMicroMission && showMissionEditor && (
            <div style={editorSection}>
              <div style={sectionTitle}>Micro-misión · Camino de Diseño Original</div>

              <div style={helperText}>
                Crea los 5 pasos de la micro-misión y el cierre final. Cada paso puede
                contener texto, preguntas, ejemplos, checklist, frase para completar o
                regla del día.
              </div>

              <div style={{ display: "grid", gap: 18 }}>
                {missionSteps.map((step) => (
                  <div
                    key={step.id}
                    style={{
                      border: "1px solid #e6ebf1",
                      borderRadius: 18,
                      padding: 16,
                      background: "#ffffff",
                    }}
                  >
                    <div style={grid2}>
                      <div>
                        <label style={labelStyle}>Paso {step.number}</label>
                        <input
                          value={step.title}
                          onChange={(e) =>
                            updateMissionStep(step.id, { title: e.target.value })
                          }
                          placeholder={`Título del paso ${step.number}`}
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                      {step.blocks.map((block, index) => (
                        <div
                          key={block.id}
                          style={{
                            display: "grid",
                            gap: 10,
                            padding: 12,
                            borderRadius: 14,
                            background: "#f8fafc",
                            border: "1px solid #edf1f5",
                          }}
                        >
                          <div style={grid2}>
                            <div>
                              <label style={labelStyle}>Tipo de bloque interno</label>
                              <select
                                value={block.kind}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    kind: e.target.value,
                                  })
                                }
                                style={inputStyle}
                              >
                                <option value="text">Texto</option>
                                <option value="question">Pregunta</option>
                                <option value="binary_question">Pregunta binaria Sí / No</option>
                                <option value="response">Campo de respuesta</option>
                                <option value="example">Selección / opciones</option>
                                <option value="choice_cards">Tarjetas de elección</option>
                                <option value="multi_select">Selección múltiple</option>
                                <option value="checklist">Checklist</option>
                                <option value="complete_phrase">
                                  Frase para completar
                                </option>
                                <option value="rule">Regla</option>
                                <option value="timer">Cronómetro</option>
                              </select>
                            </div>

                            <div>
                              <label style={labelStyle}>Etiqueta opcional</label>
                              <input
                                value={block.placeholder || ""}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    placeholder: e.target.value,
                                  })
                                }
                                placeholder="Ej: Responde con honestidad"
                                style={inputStyle}
                              />
                            </div>
                          </div>

                          <textarea
                            value={block.text}
                            onChange={(e) =>
                              updateMissionBlock(step.id, block.id, {
                                text: e.target.value,
                              })
                            }
                            rows={4}
                            placeholder={`Contenido del bloque interno ${index + 1}`}
                            style={textareaStyle}
                          />

                          <div>
                            <button
                              type="button"
                              onClick={() => removeMissionBlock(step.id, block.id)}
                              style={{
                                ...miniBtnDanger,
                                width: "fit-content",
                                padding: "8px 12px",
                                borderRadius: 10,
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              Eliminar bloque interno
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <button
                        type="button"
                        onClick={() => addMissionBlock(step.id)}
                        style={secondaryBtn}
                      >
                        Añadir bloque interno
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: 22,
                  border: "1px solid #e6ebf1",
                  borderRadius: 18,
                  padding: 16,
                  background: "#fffaf0",
                }}
              >
                <div style={sectionTitle}>Cierre final</div>

                <input
                  value={missionClosing.title}
                  onChange={(e) => updateMissionClosing({ title: e.target.value })}
                  placeholder="Ej: Altar de Testimonio"
                  style={inputStyle}
                />

                <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                  {missionClosing.blocks.map((block, index) => (
                    <div
                      key={block.id}
                      style={{
                        display: "grid",
                        gap: 10,
                        padding: 12,
                        borderRadius: 14,
                        background: "#ffffff",
                        border: "1px solid #edf1f5",
                      }}
                    >
                      <div style={grid2}>
                        <div>
                          <label style={labelStyle}>Tipo de bloque interno</label>
                          <select
                            value={block.kind}
                            onChange={(e) =>
                              updateMissionClosingBlock(block.id, {
                                kind: e.target.value,
                              })
                            }
                            style={inputStyle}
                          >
                            <option value="text">Texto</option>
                            <option value="question">Pregunta</option>
                            <option value="binary_question">Pregunta binaria Sí / No</option>
                            <option value="response">Campo de respuesta</option>
                            <option value="example">Selección / opciones</option>
                            <option value="choice_cards">Tarjetas de elección</option>
                            <option value="multi_select">Selección múltiple</option>
                            <option value="checklist">Checklist</option>
                            <option value="complete_phrase">
                              Frase para completar
                            </option>
                            <option value="rule">Regla</option>
                            <option value="timer">Cronómetro</option>
                          </select>
                        </div>

                        <div>
                          <label style={labelStyle}>Etiqueta opcional</label>
                          <input
                            value={block.placeholder || ""}
                            onChange={(e) =>
                              updateMissionClosingBlock(block.id, {
                                placeholder: e.target.value,
                              })
                            }
                            placeholder="Ej: Evidencia levantada hoy"
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      <textarea
                        value={block.text}
                        onChange={(e) =>
                          updateMissionClosingBlock(block.id, {
                            text: e.target.value,
                          })
                        }
                        rows={4}
                        placeholder={`Contenido del cierre ${index + 1}`}
                        style={textareaStyle}
                      />

                      <div>
                        <button
                          type="button"
                          onClick={() => removeMissionClosingBlock(block.id)}
                          style={{
                            ...miniBtnDanger,
                            width: "fit-content",
                            padding: "8px 12px",
                            borderRadius: 10,
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          Eliminar bloque interno
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    onClick={addMissionClosingBlock}
                    style={secondaryBtn}
                  >
                    Añadir bloque al cierre
                  </button>
                </div>
              </div>
            </div>
          )}

          {showScripture && (
            <div style={editorSection}>
              <div style={sectionTitle}>Palabra base</div>

              <input
                placeholder="Referencia bíblica"
                value={scriptureReference}
                onChange={(e) => setScriptureReference(e.target.value)}
                style={inputStyle}
              />

              <textarea
                placeholder="Texto bíblico"
                value={scriptureText}
                onChange={(e) => setScriptureText(e.target.value)}
                rows={6}
                style={textareaStyle}
              />
            </div>
          )}

          {showApplicationText && (
            <div style={editorSection}>
              <div style={sectionTitle}>Aplicación principal</div>

              <textarea
                placeholder="Guía, interpretación y dirección práctica"
                value={applicationText}
                onChange={(e) => setApplicationText(e.target.value)}
                rows={6}
                style={textareaStyle}
              />
            </div>
          )}

          {showBodyText && (
            <div style={editorSection}>
              <div style={sectionTitle}>Texto base</div>

              <textarea
                placeholder="Texto principal o introductorio"
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                rows={6}
                style={textareaStyle}
              />
            </div>
          )}

          <div style={editorSection}>
            <div style={sectionTitle}>Líneas guiadas</div>
            <div style={helperText}>
              Cada fila puede tener su propio estilo: párrafo, bullet, check,
              numerada o frase destacada. También puedes elegir un ícono
              monocromo por línea y usar <strong>**negrita**</strong> dentro del
              texto.
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {lines.map((line, index) => (
                <div key={line.id} style={lineRow}>
                  <select
                    value={line.kind}
                    onChange={(e) =>
                      updateLine(line.id, { kind: e.target.value })
                    }
                    style={smallSelect}
                  >
                    {LINE_TYPES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={line.icon}
                    onChange={(e) =>
                      updateLine(line.id, { icon: e.target.value })
                    }
                    style={smallSelect}
                  >
                    {ICON_STYLES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>

                  <input
                    value={line.text}
                    onChange={(e) =>
                      updateLine(line.id, { text: e.target.value })
                    }
                    placeholder={`Línea ${index + 1}`}
                    style={{ ...inputStyle, margin: 0 }}
                  />

                  <div style={lineActions}>
                    <button
                      type="button"
                      onClick={() => moveLine(line.id, "up")}
                      style={miniBtn}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveLine(line.id, "down")}
                      style={miniBtn}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => addLine(line.id)}
                      style={miniBtn}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLine(line.id)}
                      style={miniBtnDanger}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <button type="button" onClick={() => addLine()} style={secondaryBtn}>
                Añadir línea
              </button>
            </div>
          </div>

          <div style={editorSection}>
            <div style={grid2}>
              <div>
                <label style={labelStyle}>Estilo de frase final</label>
                <select
                  value={emphasisStyle}
                  onChange={(e) => setEmphasisStyle(e.target.value)}
                  style={inputStyle}
                >
                  {EMPHASIS_STYLES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Frase de cierre</label>
                <input
                  placeholder="Opcional"
                  value={closingText}
                  onChange={(e) => setClosingText(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            {showResourceNote && (
              <input
                placeholder="Nota opcional del recurso"
                value={resourceNote}
                onChange={(e) => setResourceNote(e.target.value)}
                style={inputStyle}
              />
            )}
          </div>

          {(type === "audio" || type === "video" || type === "download") && (
            <div style={editorSection}>
              <div style={sectionTitle}>Media vinculada</div>

              <input
                placeholder="Buscar media por nombre"
                value={mediaSearch}
                onChange={(e) => setMediaSearch(e.target.value)}
                style={inputStyle}
              />

              <div style={mediaGrid}>
                {filteredMedia.slice(0, mediaSearch.trim() ? 24 : 8).map((item) => {
                  const active = selectedMedia?.url === item.url;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setSelectedMedia(item);
                        setManualMediaUrl(item.url || "");
                      }}
                      style={{
                        ...mediaCard,
                        borderColor: active ? "#264763" : "#e6ebf1",
                        background: active ? "#f7fbff" : "#fff",
                      }}
                    >
                      <div style={mediaTitle}>{item.title}</div>
                      <div style={mediaMeta}>
                        {item.type || item.kind} {item.provider ? `· ${item.provider}` : ""}
                      </div>
                    </button>
                  );
                })}
              </div>

              <input
                placeholder="O pega manualmente la URL"
                value={manualMediaUrl}
                onChange={(e) => {
                  setManualMediaUrl(e.target.value);
                  setSelectedMedia(null);
                }}
                style={inputStyle}
              />
            </div>
          )}

          <div style={editorSection}>
            <div style={sectionTitle}>Vista previa del contenido guardado</div>
            <textarea
              value={contentPreview}
              readOnly
              rows={14}
              style={{ ...textareaStyle, background: "#f8fafc" }}
            />
          </div>

          <div style={actionRow}>
            <button style={primaryBtn} disabled={saving}>
              {saving
                ? "Guardando..."
                : editingId
                ? "Actualizar bloque"
                : "Crear bloque"}
            </button>

            {(editingId ||
              title ||
              bodyText ||
              scriptureText ||
              applicationText ||
              closingText) && (
              <button
                type="button"
                onClick={resetForm}
                style={secondaryBtn}
              >
                Limpiar
              </button>
            )}
          </div>
        </form>

        <div style={{ marginTop: 20, display: "grid", gap: 12 }}>
          {loading ? (
            <p style={{ color: "#6b7a88" }}>Cargando...</p>
          ) : items.length === 0 ? (
            <p style={{ color: "#6b7a88" }}>No hay bloques aún.</p>
          ) : (
            orderedItems.map((item) => {
              const parsedBlock = parseLessonBlockContent(item.content || "");

              return (
                <div key={item.id} style={cardStyle}>
                  <div style={{ minWidth: 0 }}>
                    <div style={itemTitle}>{item.title}</div>
                    <div style={itemMeta}>
                      Posición {item.position ?? "—"} · {item.type}
                      {parsedBlock.hasMicroMission
                        ? " · Micro-misión activa"
                        : ""}
                    </div>
                  </div>

                  <div style={actionsStyle}>
                    <button
                      type="button"
                      onClick={() => startEdit(item)}
                      style={secondaryBtn}
                    >
                      Editar bloque
                    </button>

                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      style={deleteBtn}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

/* ===== STYLES ===== */

const formBox = {
  display: "grid",
  gap: 12,
  padding: 16,
  borderRadius: 14,
  border: "1px solid #e6ebf1",
  background: "#fff",
  marginBottom: 16,
};

const grid2 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
};

const labelStyle = {
  display: "block",
  marginBottom: 5,
  fontSize: 14,
  fontWeight: 600,
  color: "#334155",
};

const inputStyle = {
  width: "100%",
  minHeight: 42,
  padding: "9px 11px",
  borderRadius: 9,
  border: "1px solid #d6dde5",
  background: "#fff",
  boxSizing: "border-box",
  fontSize: 14,
};

const textareaStyle = {
  width: "100%",
  padding: "10px 11px",
  borderRadius: 9,
  border: "1px solid #d6dde5",
  resize: "vertical",
  background: "#fff",
  boxSizing: "border-box",
  fontSize: 14,
  lineHeight: 1.5,
};

const editorSection = {
  display: "grid",
  gap: 8,
  padding: 12,
  borderRadius: 12,
  border: "1px solid #edf2f7",
  background: "#fcfdff",
};

const sectionTitle = {
  fontSize: 15,
  fontWeight: 700,
  color: "#0f2f44",
};

const helperText = {
  fontSize: 12,
  color: "#64748b",
  lineHeight: 1.45,
};

const lineRow = {
  display: "grid",
  gridTemplateColumns: "150px 140px 1fr auto",
  gap: 8,
  alignItems: "start",
};

const smallSelect = {
  width: "100%",
  minHeight: 40,
  padding: "9px 10px",
  borderRadius: 9,
  border: "1px solid #d6dde5",
  background: "#fff",
  boxSizing: "border-box",
  fontSize: 14,
};

const lineActions = {
  display: "flex",
  gap: 5,
  alignItems: "center",
};

const miniBtn = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid #d6dde5",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
};

const miniBtnDanger = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid #ead0d0",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
  color: "#9f3a38",
};

const mediaGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 8,
};

const mediaCard = {
  border: "1px solid #e6ebf1",
  borderRadius: 10,
  padding: 10,
  textAlign: "left",
  cursor: "pointer",
  background: "#fff",
};

const mediaTitle = {
  fontSize: 14,
  fontWeight: 700,
  color: "#0f2f44",
  marginBottom: 3,
};

const mediaMeta = {
  fontSize: 12,
  color: "#6b7a88",
};

const actionRow = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
};

const primaryBtn = {
  padding: "10px 14px",
  background: "#0a2230",
  color: "#fff",
  border: "none",
  borderRadius: 9,
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 14,
};

const secondaryBtn = {
  border: "1px solid #d6dde5",
  padding: "9px 12px",
  borderRadius: 9,
  background: "#fff",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
};

const cardStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  padding: 13,
  borderRadius: 12,
  border: "1px solid #e6ebf1",
  background: "#fff",
};

const itemTitle = {
  fontSize: 15,
  fontWeight: 700,
  color: "#0f2f44",
};

const itemMeta = {
  fontSize: 12,
  color: "#6b7a88",
  marginTop: 3,
};

const actionsStyle = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
};

const deleteBtn = {
  border: "1px solid #ddd",
  padding: "9px 12px",
  borderRadius: 9,
  background: "#fff",
  cursor: "pointer",
  fontSize: 14,
};